import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, HelpCircle } from "lucide-react";
import { AssistantChat } from "./AssistantChat";

interface FloatingAssistantDrawerProps {
  isOpen?: boolean;
  onToggle?: () => void;
  showFloatingButton?: boolean;
}

export const FloatingAssistantDrawer: React.FC<FloatingAssistantDrawerProps> = ({
  isOpen: controlledIsOpen,
  onToggle: controlledOnToggle,
  showFloatingButton = false,
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);

  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

  const toggleDrawer = () => {
    if (isControlled && controlledOnToggle) {
      controlledOnToggle();
    } else {
      setInternalIsOpen((prev) => !prev);
    }
  };

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        if (isControlled && controlledOnToggle) {
          controlledOnToggle();
        } else {
          setInternalIsOpen(false);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, isControlled, controlledOnToggle]);

  return (
    <>
      {/* Floating Action Button at Bottom-Right (optional, hidden by default) */}
      {showFloatingButton && !isOpen && (
        <motion.button
          type="button"
          onClick={toggleDrawer}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0, opacity: 0 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          title="Ask Proposal HQ Assistant"
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full bg-[#2ba5b5] px-4 py-3 text-sm font-semibold text-white shadow-lg transition-colors hover:bg-[#238997] focus:outline-hidden focus:ring-2 focus:ring-[#2ba5b5] focus:ring-offset-2"
        >
          <Sparkles className="h-4 w-4" />
          <span>Ask Proposal HQ</span>
        </motion.button>
      )}

      {/* Slide-over Drawer & Backdrop */}
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-50 overflow-hidden">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={toggleDrawer}
              className="absolute inset-0 bg-slate-900/40 backdrop-blur-xs transition-opacity"
            />

            {/* Slide-out Panel */}
            <div className="fixed inset-y-0 right-0 flex max-w-full pl-10">
              <motion.div
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "spring", damping: 28, stiffness: 280 }}
                className="w-screen max-w-md"
              >
                <div className="glass flex h-full flex-col border-l border-slate-200/80 bg-white shadow-2xl dark:border-slate-800/80 dark:bg-slate-900">
                  {/* Drawer Header */}
                  <div className="flex items-center justify-between border-b border-slate-200/80 px-5 py-3.5 dark:border-slate-800/80">
                    <div className="flex items-center gap-2.5">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-500/10 text-[#2ba5b5] dark:bg-teal-400/10">
                        <HelpCircle className="h-4 w-4" />
                      </div>
                      <div>
                        <h2 className="text-sm font-bold text-slate-900 dark:text-white">
                          Proposal HQ Assistant
                        </h2>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400">
                          Instant answers on features and workflows
                        </p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={toggleDrawer}
                      aria-label="Close assistant drawer"
                      className="btn-animate rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Embedded Assistant Chat */}
                  <div className="flex-1 p-3">
                    <AssistantChat isCompact={false} />
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};
